import { useEffect, useState, useCallback } from 'react';
import { Box, Stack, Typography, Button, Switch, Chip, Table, Sheet } from '@mui/joy';
import { APIFinancePayables, APIFinancePayablesDetails, APIFinancePayablesSyncAll } from '../../../api';
import {
  useFinanceToken, PeriodBar, MetricCard, MetricGrid, SectionCard, scrollTableSx, fmtMoney,
} from './financeShared';
import KonsolMismatchPanel from './KonsolMismatchPanel';


const BUCKET_LABELS = {
  no_task: 'Нет задачи', created: 'Создана', submitted: 'На подтверждении',
  confirmed: 'Подтверждена', paid: 'Оплачена', declined: 'Отклонена', other: 'Прочее',
};
const BUCKET_COLOR = {
  no_task: 'neutral', created: 'primary', submitted: 'warning',
  confirmed: 'success', paid: 'success', declined: 'danger', other: 'neutral',
};

function BucketChip({ bucket, paid }) {
  if (paid) return <Chip size="sm" variant="soft" color="success">Оплачено</Chip>;
  return <Chip size="sm" variant="soft" color={BUCKET_COLOR[bucket] || 'neutral'}>{BUCKET_LABELS[bucket] || bucket}</Chip>;
}

export default function FinancePayablesTab() {
  const token = useFinanceToken();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusBucket, setStatusBucket] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState({ results: [], pagination: {} });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const baseParams = () => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    status_bucket: statusBucket || undefined,
    overdue_only: overdueOnly ? 'true' : undefined,
  });

  const loadSummary = useCallback(() => {
    if (!token) return;
    APIFinancePayables(baseParams(), token).then(setSummary).catch((e) => console.error('payables', e));
  }, [token, dateFrom, dateTo, statusBucket, overdueOnly]);

  const loadDetails = useCallback(() => {
    if (!token) return;
    setLoading(true);
    APIFinancePayablesDetails({ ...baseParams(), page }, token)
      .then((d) => setDetails(d || { results: [], pagination: {} }))
      .catch((e) => console.error('payables details', e))
      .finally(() => setLoading(false));
  }, [token, dateFrom, dateTo, statusBucket, overdueOnly, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadDetails(); }, [loadDetails]);
  useEffect(() => { setPage(1); }, [dateFrom, dateTo, statusBucket, overdueOnly]);

  const sync = () => {
    setSyncing(true);
    APIFinancePayablesSyncAll(token)
      .then(() => { loadSummary(); loadDetails(); })
      .catch((e) => console.error(e))
      .finally(() => setSyncing(false));
  };

  const t = summary?.totals || {};

  return (
    <Box>
      <KonsolMismatchPanel />
      <PeriodBar
        from={dateFrom} to={dateTo}
        onFrom={setDateFrom} onTo={setDateTo}
        loading={loading}
        right={
          <>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography level="body-sm" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Только просроченные</Typography>
              <Switch checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
            </Stack>
            <Button size="sm" variant="outlined" color="neutral" loading={syncing} onClick={sync} sx={{ borderRadius: 'lg' }}>
              Синхронизировать статусы
            </Button>
          </>
        }
      />

      <MetricGrid>
        <MetricCard index={0} title="Всего к выплате" value={fmtMoney(t.outstanding)} color="#0ea5e9" />
        <MetricCard index={1} title={`Просрочено > ${t.overdue_days ?? 15} дней`} value={fmtMoney(t.overdue)} color="#ef4444" />
        <MetricCard index={2} title="Висит в Консоли" value={fmtMoney(t.in_konsol)} color="#8b5cf6" />
        <MetricCard index={3} title="Ждёт объединения" value={fmtMoney(t.awaiting_consolidation)} color="#f59e0b" />
      </MetricGrid>

      {/* Бакеты статусов Консоли — клик фильтрует детализацию */}
      <SectionCard title="По статусам Консоли (клик — фильтр)">
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip size="lg" variant={statusBucket === '' ? 'solid' : 'soft'} color="neutral"
            onClick={() => setStatusBucket('')} sx={{ cursor: 'pointer', borderRadius: 'xl', fontWeight: 600 }}>
            Все
          </Chip>
          {(summary?.by_status_bucket || []).map((b) => (
            <Chip key={b.bucket} size="lg"
              variant={statusBucket === b.bucket ? 'solid' : 'soft'}
              color={BUCKET_COLOR[b.bucket] || 'neutral'}
              onClick={() => setStatusBucket(b.bucket)}
              sx={{ cursor: 'pointer', borderRadius: 'xl', fontWeight: 600 }}>
              {BUCKET_LABELS[b.bucket] || b.bucket}: {fmtMoney(b.sum)} ({b.count})
            </Chip>
          ))}
        </Box>
      </SectionCard>

      <SectionCard title="Детализация">
        <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto' }}>
          <Table borderAxis="both" stickyHeader hoverRow size="sm" sx={scrollTableSx(760)}>
            <thead>
              <tr>
                <th style={{ width: 80 }}>Сделка</th>
                <th>Обмерщик</th>
                <th>Чертёжник</th>
                <th style={{ width: 120, textAlign: 'right' }}>Долг, ₽</th>
                <th style={{ width: 90, textAlign: 'center' }}>Возраст</th>
                <th style={{ width: 150 }}>Статус (обм.)</th>
                <th style={{ width: 150 }}>Статус (черт.)</th>
              </tr>
            </thead>
            <tbody>
              {(details.results || []).map((r) => (
                <tr key={r.payment_id}>
                  <td>#{r.deal_id ?? '—'}</td>
                  <td>{r.surveyor || '—'}{r.is_surveyor_paid ? ' ✓' : ''}</td>
                  <td>{r.draftsman || '—'}{r.is_draftsman_paid ? ' ✓' : ''}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.outstanding)}</td>
                  <td style={{ textAlign: 'center', color: r.is_overdue ? '#ef4444' : undefined }}>{r.age_days} дн.</td>
                  <td><BucketChip bucket={r.surveyor_bucket} paid={r.is_surveyor_paid} /></td>
                  <td><BucketChip bucket={r.draftsman_bucket} paid={r.is_draftsman_paid} /></td>
                </tr>
              ))}
              {(!details.results || details.results.length === 0) && !loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>Нет кредиторки</td></tr>
              )}
            </tbody>
          </Table>
        </Sheet>

        {details.pagination?.total_pages > 1 && (
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1.5 }}>
            <Button size="sm" variant="outlined" disabled={!details.pagination.previous} onClick={() => setPage((p) => Math.max(1, p - 1))}>Назад</Button>
            <Typography level="body-sm">{details.pagination.current_page} / {details.pagination.total_pages}</Typography>
            <Button size="sm" variant="outlined" disabled={!details.pagination.next} onClick={() => setPage((p) => p + 1)}>Вперёд</Button>
          </Stack>
        )}
      </SectionCard>
    </Box>
  );
}